/**
 * Step Gamer header interactions:
 * sticky/transparent, megamenu, mobile menu, search, mini-cart
 */
if (!customElements.get('step-gamer-header')) {
  class StepGamerHeader extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.activeMega = null;
    }

    connectedCallback() {
      this.nav = this.querySelector('[data-sg-nav]');
      this.pill = this.querySelector('[data-sg-pill]');
      this.mega = this.querySelector('[data-sg-mega]');
      this.megaBackdrop = this.querySelector('[data-sg-mega-backdrop]');
      this.mobile = this.querySelector('[data-sg-mobile]');
      this.search = this.querySelector('[data-sg-search]');
      this.cartWrap = this.querySelector('[data-sg-cart-wrap]');
      this._megaCloseTimer = null;

      this.querySelectorAll('[data-sg-nav-item]').forEach((el) => {
        const onEnter = () => {
          if (el.hasAttribute('data-sg-mega-trigger')) this.openMega(el);
          else this.closeMega();
        };
        el.addEventListener('mouseenter', onEnter);
        el.addEventListener('focusin', onEnter);
      });

      if (this.mega) {
        this.mega.addEventListener('mouseenter', () => this.cancelMegaClose());
        this.mega.addEventListener('mouseleave', () => this.scheduleMegaClose());
      }

      this.addEventListener('mouseenter', () => {
        this.classList.add('is-hovered');
      });
      this.addEventListener('mouseleave', () => {
        this.classList.remove('is-hovered');
        this.scheduleMegaClose();
      });

      this.querySelectorAll('[data-sg-mobile-open]').forEach((btn) => {
        btn.addEventListener('click', () => this.openMobile());
      });
      this.querySelectorAll('[data-sg-mobile-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeMobile());
      });
      if (this.mobile) {
        this.mobile.addEventListener('click', (e) => {
          if (e.target === this.mobile) this.closeMobile();
        });
        this.initMobileLevels();
      }

      this.querySelectorAll('[data-sg-search-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => this.toggleSearch());
      });
      this.querySelectorAll('[data-sg-search-cancel]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeSearch());
      });
      this.initSearch();

      this.querySelectorAll('[data-sg-cart-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleCart();
        });
      });
      this.querySelectorAll('[data-sg-cart-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeCart());
      });

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKeydown);
      this.onScroll();
      this.syncHeaderHeight();
      const logoImg = this.querySelector('.step-gamer-header__logo img');
      if (logoImg && !logoImg.complete) {
        logoImg.addEventListener('load', () => this.syncHeaderHeight(), { once: true });
      }
    }

    disconnectedCallback() {
      clearTimeout(this._cartCloseTimer);
      clearTimeout(this._mobileCloseTimer);
      this.unpinCartDrawer();
      this.unpinMobileDrawer();
      this.unpinSearchOverlay();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    onScroll() {
      const y = window.scrollY || 0;
      this.classList.toggle('is-scrolled', y > 20);
    }

    syncHeaderHeight() {
      const bar = this.querySelector('.step-gamer-header__bar');
      if (!bar) return;
      const height = Math.ceil(bar.getBoundingClientRect().height);
      if (height > 0) {
        document.documentElement.style.setProperty('--sg-header-height', `${height}px`);
      }
    }

    onResize() {
      this.syncHeaderHeight();
      if (window.matchMedia('(min-width: 1280px)').matches) {
        this.closeMobile();
      } else {
        this.closeMega();
      }
      if (this.search && this.search.classList.contains('is-open')) {
        if (window.matchMedia('(max-width: 991px)').matches) this.pinSearchOverlay();
        else this.unpinSearchOverlay();
      }
    }

    onDocClick(e) {
      if (this.search && this.search.classList.contains('is-open') && !this.search.contains(e.target) && !e.target.closest('[data-sg-search-toggle]')) {
        this.closeSearch();
      }
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.closeMega();
        this.closeMobile();
        this.closeSearch();
        this.closeCart();
      }
    }

    setScrollLock(on) {
      document.documentElement.classList.toggle('step-gamer-scroll-locked', on);
    }

    /* ----- Megamenu ----- */
    cancelMegaClose() {
      if (this._megaCloseTimer) {
        clearTimeout(this._megaCloseTimer);
        this._megaCloseTimer = null;
      }
    }

    scheduleMegaClose() {
      this.cancelMegaClose();
      this._megaCloseTimer = setTimeout(() => this.closeMega(), 120);
    }

    openMega(trigger) {
      if (!this.mega || !window.matchMedia('(min-width: 1280px)').matches) return;
      this.cancelMegaClose();
      const key = trigger.getAttribute('data-sg-mega-trigger');
      const panel = this.mega.querySelector('[data-sg-mega-panel="' + key + '"]');

      this.querySelectorAll('[data-sg-mega-trigger]').forEach((el) => {
        el.classList.toggle('is-active', el === trigger);
      });

      if (!panel) {
        this.closeMega(false);
        return;
      }

      this.classList.add('is-hovered', 'is-open');
      this.mega.querySelectorAll('[data-sg-mega-panel]').forEach((p) => {
        p.hidden = p !== panel;
      });
      this.mega.classList.add('is-open');
      this.mega.setAttribute('aria-hidden', 'false');
      if (this.megaBackdrop) this.megaBackdrop.classList.add('is-open');

      requestAnimationFrame(() => {
        const h = Math.max(panel.scrollHeight, panel.offsetHeight) + 32;
        this.mega.style.height = h + 'px';
      });
      this.activeMega = key;
    }

    closeMega(clearActive = true) {
      if (!this.mega) return;
      this.cancelMegaClose();
      this.mega.style.height = '0px';
      this.mega.classList.remove('is-open');
      this.mega.setAttribute('aria-hidden', 'true');
      if (this.megaBackdrop) this.megaBackdrop.classList.remove('is-open');
      this.classList.remove('is-open');
      if (clearActive) {
        this.querySelectorAll('[data-sg-mega-trigger]').forEach((el) => el.classList.remove('is-active'));
      }
      this.activeMega = null;
    }

    /* ----- Mobile ----- */
    pinMobileDrawer() {
      if (!this.mobile || this.mobile.parentElement === document.body) return;
      this._mobileParent = this.mobile.parentNode;
      this._mobileNext = this.mobile.nextSibling;
      document.body.appendChild(this.mobile);
    }

    unpinMobileDrawer() {
      if (!this.mobile || !this._mobileParent) return;
      this._mobileParent.insertBefore(this.mobile, this._mobileNext);
      this._mobileParent = null;
      this._mobileNext = null;
    }

    openMobile() {
      if (!this.mobile) return;
      clearTimeout(this._mobileCloseTimer);
      this.closeCart();
      this.closeSearch();
      this.pinMobileDrawer();
      this.mobile.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      this.setScrollLock(true);
      this.resetMobileLevels();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.mobile) this.mobile.classList.add('is-open');
        });
      });
    }

    closeMobile() {
      if (!this.mobile) return;
      this.mobile.classList.remove('is-open');
      this.mobile.setAttribute('aria-hidden', 'true');
      this.classList.remove('is-open');
      this.setScrollLock(false);
      this.resetMobileLevels();
      clearTimeout(this._mobileCloseTimer);
      this._mobileCloseTimer = setTimeout(() => {
        if (!this.mobile?.classList.contains('is-open')) this.unpinMobileDrawer();
      }, 400);
    }

    initMobileLevels() {
      this.mobile.querySelectorAll('[data-sg-mobile-drill]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const target = btn.getAttribute('data-sg-mobile-drill');
          const level = this.mobile.querySelector('[data-sg-mobile-panel="' + target + '"]');
          const root = this.mobile.querySelector('[data-level="0"]');
          if (!level) return;
          if (root) root.classList.add('is-pushed');
          level.classList.add('is-active');
        });
      });
      this.mobile.querySelectorAll('[data-sg-mobile-back]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const panel = btn.closest('[data-sg-mobile-panel]');
          const root = this.mobile.querySelector('[data-level="0"]');
          if (panel) panel.classList.remove('is-active');
          if (root) root.classList.remove('is-pushed');
        });
      });
    }

    resetMobileLevels() {
      if (!this.mobile) return;
      this.mobile.querySelectorAll('[data-sg-mobile-panel]').forEach((p) => p.classList.remove('is-active'));
      const root = this.mobile.querySelector('[data-level="0"]');
      if (root) root.classList.remove('is-pushed');
    }

    /* ----- Search ----- */
    toggleSearch() {
      if (!this.search) return;
      if (this.search.classList.contains('is-open')) this.closeSearch();
      else this.openSearch();
    }

    pinSearchOverlay() {
      if (!this.search || this.search.parentElement === document.body) return;
      this._searchParent = this.search.parentNode;
      this._searchNext = this.search.nextSibling;
      document.body.appendChild(this.search);
    }

    unpinSearchOverlay() {
      if (!this.search || !this._searchParent) return;
      this._searchParent.insertBefore(this.search, this._searchNext);
      this._searchParent = null;
      this._searchNext = null;
    }

    openSearch() {
      if (!this.search) return;
      this.closeMega();
      this.closeCart();
      if (window.matchMedia('(max-width: 991px)').matches) {
        this.pinSearchOverlay();
        this.setScrollLock(true);
      }
      this.search.classList.add('is-open');
      this.classList.add('is-open');
      const input = this.search.querySelector('input[type="search"], input[name="q"]');
      if (input) setTimeout(() => input.focus(), 50);
    }

    closeSearch() {
      if (!this.search) return;
      this.search.classList.remove('is-open', 'is-loading');
      this.classList.remove('is-open');
      this.unpinSearchOverlay();
      this.setScrollLock(false);
    }

    initSearch() {
      if (!this.search) return;
      const input = this.search.querySelector('input[name="q"]');
      const results = this.search.querySelector('[data-sg-search-results]');
      const suggestions = this.search.querySelector('[data-sg-search-suggestions]');
      if (!input || !results) return;

      let timer = null;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        const q = input.value.trim();
        if (q.length < 2) {
          results.innerHTML = '';
          if (suggestions) suggestions.hidden = false;
          return;
        }
        timer = setTimeout(() => this.fetchPredictive(q, results, suggestions), 250);
      });
    }

    fetchPredictive(q, results, suggestions) {
      this.search.classList.add('is-loading');
      const url =
        window.routes && window.routes.predictive_search_url
          ? window.routes.predictive_search_url
          : '/search/suggest';
      const section = this.search.getAttribute('data-section-id') || 'step-gamer-predictive-search';
      fetch(url + '?q=' + encodeURIComponent(q) + '&resources[type]=product&section_id=' + section)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('#shopify-section-' + section) || doc.querySelector('[data-sg-predictive-root]');
          results.innerHTML = content ? content.innerHTML : html;
          if (suggestions) suggestions.hidden = true;
        })
        .catch(() => {
          results.innerHTML = '';
        })
        .finally(() => {
          this.search.classList.remove('is-loading');
        });
    }

    /* ----- Cart ----- */
    cartDrawer() {
      return document.querySelector('[data-sg-cart-section]');
    }

    syncCartToggle(open) {
      this.querySelectorAll('[data-sg-cart-toggle]').forEach((btn) => {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    pinCartDrawer() {
      const mini = this.cartDrawer();
      if (!mini || mini.parentElement === document.body) return;
      this._cartParent = mini.parentNode;
      this._cartNext = mini.nextSibling;
      document.body.appendChild(mini);
    }

    unpinCartDrawer() {
      const mini = this.cartDrawer();
      if (!mini || !this._cartParent) return;
      this._cartParent.insertBefore(mini, this._cartNext);
      this._cartParent = null;
      this._cartNext = null;
    }

    toggleCart() {
      const mini = this.cartDrawer();
      if (!mini) return;
      if (mini.classList.contains('is-open')) this.closeCart();
      else this.openCart();
    }

    openCart() {
      const mini = this.cartDrawer();
      if (!mini) return;
      clearTimeout(this._cartCloseTimer);
      this.closeMega();
      this.closeSearch();
      this.closeMobile();
      this.pinCartDrawer();
      mini.classList.add('is-open');
      mini.setAttribute('aria-hidden', 'false');
      this.cartWrap?.classList.add('is-open');
      this.classList.add('is-open');
      this.syncCartToggle(true);
      this.setScrollLock(true);
      const closeBtn = mini.querySelector('.step-gamer-cart-mini__close');
      if (closeBtn) setTimeout(() => closeBtn.focus(), 50);
    }

    closeCart() {
      const mini = this.cartDrawer();
      if (!mini || !mini.classList.contains('is-open')) {
        this.syncCartToggle(false);
        return;
      }
      mini.classList.remove('is-open');
      mini.setAttribute('aria-hidden', 'true');
      this.cartWrap?.classList.remove('is-open');
      this.classList.remove('is-open');
      this.syncCartToggle(false);
      this.setScrollLock(false);
      clearTimeout(this._cartCloseTimer);
      this._cartCloseTimer = setTimeout(() => {
        if (!this.cartDrawer()?.classList.contains('is-open')) this.unpinCartDrawer();
      }, 400);
    }
  }

  customElements.define('step-gamer-header', StepGamerHeader);
}

/* Cart badge + mini-cart refresh helpers */
window.StepGamerCart = {
  sectionId: 'step-gamer-cart-mini',
  updateBadges(count) {
    document.querySelectorAll('[data-sg-cart-badge]').forEach((el) => {
      el.textContent = count > 0 ? String(count) : '';
      el.setAttribute('data-count', String(count || 0));
    });
  },
  open() {
    const header = document.querySelector('step-gamer-header');
    if (header && typeof header.openCart === 'function') header.openCart();
  },
  refresh() {
    const current = document.querySelector('[data-sg-cart-section]');
    if (!current) return Promise.resolve();
    const wasOpen = current.classList.contains('is-open');
    return fetch(window.Shopify.routes.root + '?section_id=' + this.sectionId)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('[data-sg-cart-section]');
        if (!next) return;
        current.innerHTML = next.innerHTML;
        current.classList.toggle('is-open', wasOpen);
        current.setAttribute('aria-hidden', wasOpen ? 'false' : 'true');
        const countEl = current.querySelector('[data-sg-cart-count]');
        if (countEl) this.updateBadges(parseInt(countEl.getAttribute('data-sg-cart-count') || '0', 10));
      });
  },
  change(line, quantity) {
    return fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    })
      .then((r) => r.json())
      .then((cart) => {
        this.updateBadges(cart.item_count);
        return this.refresh();
      });
  },
  clear() {
    if (this._clearing) return Promise.resolve();
    this._clearing = true;
    return fetch(window.Shopify.routes.root + 'cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    })
      .then((r) => r.json())
      .then((cart) => {
        this.updateBadges(cart.item_count || 0);
        if (document.body.classList.contains('template-cart')) {
          window.location.reload();
          return;
        }
        return this.refresh();
      })
      .finally(() => {
        this._clearing = false;
      });
  },
};

document.addEventListener('click', (e) => {
  const closeBtn = e.target.closest('[data-sg-cart-close]');
  if (closeBtn) {
    const header = document.querySelector('step-gamer-header');
    if (header && typeof header.closeCart === 'function') header.closeCart();
    return;
  }

  const removeBtn = e.target.closest('[data-sg-cart-remove]');
  if (removeBtn) {
    e.preventDefault();
    const line = parseInt(removeBtn.getAttribute('data-line'), 10);
    window.StepGamerCart.change(line, 0);
    return;
  }

  const clearBtn = e.target.closest('[data-sg-cart-clear]');
  if (clearBtn) {
    e.preventDefault();
    window.StepGamerCart.clear();
    return;
  }

  const qtyBtn = e.target.closest('[data-sg-cart-qty]');
  if (!qtyBtn) return;
  e.preventDefault();
  const line = parseInt(qtyBtn.getAttribute('data-line'), 10);
  const delta = parseInt(qtyBtn.getAttribute('data-sg-cart-qty'), 10);
  const input = qtyBtn.parentElement.querySelector('input');
  const next = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
  input.value = next;
  window.StepGamerCart.change(line, next);
});
